import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileCheck, Scale, DollarSign, Plus, ShieldCheck } from "lucide-react";

const TABS = [
  { id: "agreements", label: "Agreements", icon: FileCheck },
  { id: "disputes", label: "Disputes", icon: Scale },
  { id: "coi", label: "CoI", icon: ShieldCheck },
  { id: "fx", label: "FX rates", icon: DollarSign },
];

export default function AdminGovernance() {
  const [tab, setTab] = useState("agreements");
  return (
    <div data-testid="admin-governance" className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2 flex items-center gap-2">
          <Scale size={12}/> HQ · Governance
        </div>
        <h1 className="text-3xl font-semibold text-white tracking-tight">Contract-grade controls</h1>
      </div>
      <nav className="flex gap-1 mb-6 border-b border-white/10">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-gov-tab-${t.id}`}
                  className={`text-xs uppercase tracking-[0.18em] font-mono px-4 py-3 border-b-2 ${
                    tab === t.id ? "border-[#D4B878] text-white" : "border-transparent text-white/40 hover:text-white/70"
                  }`}>
            <t.icon size={11} className="inline mr-1.5"/> {t.label}
          </button>
        ))}
      </nav>
      {tab === "agreements" && <AgreementsTab/>}
      {tab === "disputes" && <DisputesTab/>}
      {tab === "coi" && <CoITab/>}
      {tab === "fx" && <FxTab/>}
    </div>
  );
}

function AgreementsTab() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ kind:"master_partner_terms", version:"", title:"", summary:"", body_md:"" });
  const load = () => api.get("/admin/agreements").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const publish = async () => {
    try {
      await api.post("/admin/agreements", form);
      toast.success("Published.");
      setShow(false); setForm({ ...form, version:"", title:"", summary:"", body_md:"" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setShow(!show)} data-testid="admin-publish-agreement-btn"
                className="rounded-full bg-[#CCFF00] text-black font-semibold text-xs">
          <Plus size={13} className="mr-1"/> Publish version
        </Button>
      </div>
      {show && (
        <div data-testid="admin-agreement-form" className="border border-white/10 rounded-lg p-5 bg-white/[0.02] space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <select value={form.kind} onChange={(e)=>setForm({...form,kind:e.target.value})} data-testid="admin-agreement-kind"
                    className="bg-white/5 border border-white/10 text-xs rounded px-2 py-1.5 text-white font-mono">
              {["master_partner_terms","sales_addendum","advertising_addendum","location_addendum","technology_addendum","regional_addendum","privacy_dpa","coi_policy"].map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <Input placeholder="Version e.g. 1.0.0" value={form.version} onChange={(e)=>setForm({...form,version:e.target.value})} className="bg-white/5 border-white/10" data-testid="admin-agreement-version"/>
            <Input placeholder="Title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="bg-white/5 border-white/10" data-testid="admin-agreement-title"/>
          </div>
          <Input placeholder="Summary" value={form.summary} onChange={(e)=>setForm({...form,summary:e.target.value})} className="bg-white/5 border-white/10"/>
          <textarea rows={6} placeholder="Markdown body (min 50 chars)" value={form.body_md} onChange={(e)=>setForm({...form,body_md:e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded p-2 text-sm text-white font-mono resize-none"
                    data-testid="admin-agreement-body"/>
          <Button onClick={publish} data-testid="admin-agreement-publish"
                  className="rounded-full bg-[#CCFF00] text-black font-semibold text-xs">Publish</Button>
        </div>
      )}
      <div className="border border-white/10 rounded-lg bg-white/[0.02] divide-y divide-white/5">
        {items.length === 0 && <div data-testid="admin-agreements-empty" className="p-6 text-center text-white/40 text-sm">No agreements yet.</div>}
        {items.map((a) => (
          <div key={a.id} data-testid={`admin-agreement-${a.id}`} className="px-4 py-3 flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{a.title}</div>
              <div className="text-[11px] text-white/40 font-mono truncate">{a.kind} · v{a.version}</div>
            </div>
            <span className={`text-[10px] font-mono uppercase tracking-[0.14em] ${a.is_current ? "text-[#CCFF00]" : "text-white/40"}`}>
              {a.is_current ? "current" : "superseded"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DisputesTab() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ status:"investigating", decision:"", note:"" });
  const load = () => api.get("/admin/disputes").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const transition = async () => {
    try {
      const payload = { status: form.status, note: form.note };
      if (form.status === "resolved") payload.decision = form.decision || "no_action";
      await api.post(`/admin/disputes/${sel.id}/transition`, payload);
      toast.success(`Moved to ${form.status}.`);
      setSel(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  return (
    <>
      <div className="border border-white/10 rounded-lg bg-white/[0.02] divide-y divide-white/5">
        {items.length === 0 && <div data-testid="admin-disputes-empty" className="p-6 text-center text-white/40 text-sm">No open disputes.</div>}
        {items.map((d) => (
          <button key={d.id} onClick={() => setSel(d)} data-testid={`admin-dispute-${d.id}`}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] text-left">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{d.subject || d.kind}</div>
              <div className="text-[11px] text-white/40 font-mono truncate">
                {d.kind} · {d.claimant_email || d.customer_email || "—"}
              </div>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#E8D6A3]">{d.status}</span>
          </button>
        ))}
      </div>
      {sel && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setSel(null)}>
          <div className="bg-[#141110] border border-white/15 rounded-lg max-w-xl w-full p-6 my-10 space-y-3" onClick={(e)=>e.stopPropagation()}>
            <div className="text-white font-semibold">{sel.subject || sel.kind}</div>
            <div className="text-xs text-white/60 whitespace-pre-wrap">{sel.description || "(auto-generated from attribution conflict)"}</div>
            <div className="grid grid-cols-2 gap-2">
              <select value={form.status} onChange={(e)=>setForm({...form,status:e.target.value})} data-testid="admin-dispute-status"
                      className="bg-white/5 border border-white/10 text-xs rounded px-2 py-1.5 text-white font-mono">
                {["open","investigating","awaiting_response","resolved","closed"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {form.status === "resolved" && (
                <select value={form.decision} onChange={(e)=>setForm({...form,decision:e.target.value})} data-testid="admin-dispute-decision"
                        className="bg-white/5 border border-white/10 text-xs rounded px-2 py-1.5 text-white font-mono">
                  <option value="">Decision…</option>
                  {["for_claimant","for_incumbent","split","no_action"].map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              )}
            </div>
            <textarea rows={2} placeholder="Note" value={form.note} onChange={(e)=>setForm({...form,note:e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded p-2 text-sm text-white resize-none"/>
            <Button onClick={transition} data-testid="admin-dispute-transition"
                    className="w-full rounded-full bg-[#CCFF00] text-black font-semibold">Apply</Button>
          </div>
        </div>
      )}
    </>
  );
}

function CoITab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/coi").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const review = async (id, status) => {
    try {
      await api.post(`/admin/coi/${id}/review`, { status });
      toast.success("Reviewed.");
      load();
    } catch { toast.error("Failed."); }
  };
  return (
    <div className="border border-white/10 rounded-lg bg-white/[0.02] divide-y divide-white/5">
      {items.length === 0 && <div data-testid="admin-coi-empty" className="p-6 text-center text-white/40 text-sm">No CoI disclosures yet.</div>}
      {items.map((c) => (
        <div key={c.id} data-testid={`admin-coi-${c.id}`} className="px-4 py-3 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate">{c.title}</div>
            <div className="text-[11px] text-white/40 font-mono truncate">{c.kind} · {c.partner_email}</div>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/60">{c.status}</span>
          {c.status === "open" && (
            <>
              <Button onClick={() => review(c.id, "cleared")} data-testid={`admin-coi-clear-${c.id}`}
                      className="rounded-full bg-[#CCFF00] text-black text-[10px] h-auto px-2 py-1">Clear</Button>
              <Button onClick={() => review(c.id, "requires_action")} data-testid={`admin-coi-action-${c.id}`}
                      className="rounded-full border border-amber-400/40 bg-transparent text-amber-300 text-[10px] h-auto px-2 py-1">Action</Button>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function FxTab() {
  const [data, setData] = useState({ items:[], latest_by_pair:{} });
  const [form, setForm] = useState({ base:"INR", quote:"USD", rate:"", note:"" });
  const load = () => api.get("/admin/fx-rates").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.post("/admin/fx-rates", { ...form, rate: Number(form.rate) });
      toast.success("Rate set.");
      setForm({ ...form, rate:"", note:"" });
      load();
    } catch { toast.error("Failed."); }
  };
  const pairs = Object.values(data.latest_by_pair);
  return (
    <div className="space-y-4">
      <div data-testid="admin-fx-form" className="border border-white/10 rounded-lg p-4 bg-white/[0.02]">
        <div className="grid grid-cols-5 gap-2">
          <select value={form.base} onChange={(e)=>setForm({...form,base:e.target.value})} data-testid="admin-fx-base" className="bg-white/5 border border-white/10 text-xs rounded px-2 py-1.5 text-white font-mono">
            {["INR","USD","EUR","GBP","AED","SGD","AUD","CAD"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={form.quote} onChange={(e)=>setForm({...form,quote:e.target.value})} data-testid="admin-fx-quote" className="bg-white/5 border border-white/10 text-xs rounded px-2 py-1.5 text-white font-mono">
            {["INR","USD","EUR","GBP","AED","SGD","AUD","CAD"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <Input placeholder="Rate" value={form.rate} onChange={(e)=>setForm({...form,rate:e.target.value})} className="bg-white/5 border-white/10 text-xs" data-testid="admin-fx-rate"/>
          <Input placeholder="Note" value={form.note} onChange={(e)=>setForm({...form,note:e.target.value})} className="bg-white/5 border-white/10 text-xs col-span-1"/>
          <Button onClick={save} data-testid="admin-fx-save" className="rounded-full bg-[#CCFF00] text-black text-xs font-semibold">Set</Button>
        </div>
      </div>
      <div className="border border-white/10 rounded-lg bg-white/[0.02] divide-y divide-white/5">
        {pairs.length === 0 && <div data-testid="admin-fx-empty" className="p-6 text-center text-white/40 text-sm">No rates yet.</div>}
        {pairs.map((p) => (
          <div key={`${p.base}-${p.quote}`} data-testid={`admin-fx-pair-${p.base}-${p.quote}`} className="px-4 py-3 flex items-center gap-2">
            <div className="flex-1">
              <div className="text-sm font-mono text-white">{p.base} → {p.quote}</div>
              <div className="text-[11px] text-white/40 font-mono">{p.rate} · set {String(p.set_at).slice(0,10)} by {p.set_by}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
